import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { errorLog } from "../../utils/logger";
import db, { Key } from "../../database/database";
import { UserInfo } from "../../models/Interfaces";
import { ErrorModal } from "../../components/modals/ErrorModal";

export const VerifyPhoneSchema = yup.object().shape({
  phone: yup.string().required().min(14).max(14),
});

export function VerifyPhone({
  afterSubmit,
  autoFocusInput,
}: {
  afterSubmit?: (phone?: string) => void;
  autoFocusInput?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname, state } = useLocation();

  const [number, setNumber] = useState("");
  const [submittingPhoneNumber, setSubmittingPhoneNumber] = useState(false);
  const [formattedNumber, setFormattedNumber] = useState("");
  const [sendFromSettings, setSendFromSettings] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean | string>(false);

  const mobileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocusInput && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [autoFocusInput]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(VerifyPhoneSchema),
  });

  const { ref: registerRef, ...rest } = register("phone");

  const searchFormTranslations = {
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
  };

  const onSendFromSettings = async (data: any) => {
    try {
      setSubmittingPhoneNumber(true);
      const user = await db().getItem<UserInfo>(Key.UserInfo);
      if (user && user.verified_phone_number === number) {
        setShowErrorMessage(
          t("onboarding.phone_already_verified", { phoneNumber: formattedNumber })
        );
        return;
      }

      if (number.length !== 10) {
        return;
      }

      await getAPIClient().addUserAccountPhone({ phone_number: number });

      if (afterSubmit) {
        afterSubmit();
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    } finally {
      setSubmittingPhoneNumber(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setSubmittingPhoneNumber(true);
      const user = await db().getItem<UserInfo>(Key.UserInfo);
      if (user && user.verified_phone_number === number) {
        setShowErrorMessage(
          t("onboarding.phone_already_verified", { phoneNumber: formattedNumber })
        );
        return;
      }

      if (number.length !== 10) {
        return;
      }

      await getAPIClient().addUserAccountPhone({ phone_number: number });

      if (sendFromSettings || pathname === "/home/settings/phone") {
        if (afterSubmit) {
          afterSubmit(number);
        }
      } else {
        navigate("/signup/enterpin", { state: { phone: number } });
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    } finally {
      setSubmittingPhoneNumber(false);
    }
  };

  function onTextChange(text: string) {
    const cleaned = ("" + text).replace(/\D/g, "");
    const truncatedNumber = cleaned.substring(0, 10);
    setNumber(truncatedNumber);
    if (truncatedNumber.length < 11) {
      const match = truncatedNumber.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        const intlCode = match[1] ? "+1 " : "";
        const number = [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
        setFormattedNumber(number);
        return;
      }
      setFormattedNumber(truncatedNumber);
    }
  }

  const Checkmark = () => (
    <span>
      <i className="fa-solid fa-circle-check has-text-success"></i>
    </span>
  );

  const getPhoneLayout = () => {
    if (pathname === "/signup/verifyphone" || pathname === "/home/settings/phone") {
      // sign up flow
      return (
        <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col p-8 max-md:p-4">
          {showErrorMessage && (
            <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
              {showErrorMessage}
            </ErrorModal>
          )}
          <div className="flex flex-col max-md:gap-12">
            {pathname === "/signup/verifyphone" && (
              <>
                <p
                  className="md:hidden text-white cursor-pointer mobile-text-color"
                  onClick={() => navigate("/home/search")}
                >
                  {t("generic.skip")}
                </p>
                <div className="flex flex-col">
                  <p className="text-center text-2xl md:text-black mobile-text-color md:font-[700] max-md:text-center">
                    {t("onboarding.signup_header")}
                  </p>
                  <div className="separator max-md:hidden"></div>
                </div>
              </>
            )}

            <div className="flex flex-col gap-4">
              <p className="text-dark mobile-text-color text-center text-lg font-semibold max-md:mb-8">
                {t("onboarding.verify-enter-phone-desc")}
              </p>
              <div className="relative">
                <input
                  {...rest}
                  className="input border border-black text-black w-full max-md:!placeholder-dark-blue max-md:dark:!placeholder-white max-md:text-lg max-md:border-border-gray max-md:py-8 max-md:text-white"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={t("onboarding.verify-enter-phone-input")}
                  value={formattedNumber}
                  ref={(el) => {
                    registerRef(el);
                    if (mobileInputRef) mobileInputRef.current = el;
                  }}
                  onChange={(event) => onTextChange(event.target.value)}
                  data-testid="phone-input"
                />
                {formattedNumber.length === 14 && (
                  <img
                    src="/assets/images/green_status.png"
                    className="w-[20px] h-[20px] absolute right-3 top-1/2 transform -translate-y-1/2"
                  />
                )}
              </div>
              <div className="flex md:hidden min-w-full">
                <button
                  disabled={submittingPhoneNumber || formattedNumber.length !== 14}
                  className="btn md:hidden w-[80px] text-white !ml-auto docket-button-responsive"
                  type="submit"
                  data-testid="next-mobile"
                >
                  {t("generic.next")}
                </button>
              </div>
              <p className="text-dark mobile-text-color text-xs">
                {t("onboarding.verify-enter-phone-small-print-skip")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-8 mt-auto max-md:hidden">
            <button
              disabled={submittingPhoneNumber || formattedNumber.length !== 14}
              className="docket-button btn"
              type="submit"
              data-testid="next-desktop"
            >
              {t("generic.next")}
            </button>
            {pathname === "/signup/verifyphone" && (
              <div className="mx-auto">
                <a
                  className="docket-link cursor-pointer no-underline"
                  onClick={() => navigate("/home/search")}
                >
                  {t("generic.skip")}
                </a>
              </div>
            )}
          </div>
        </form>
      );
    } else if (pathname === "/home/settings") {
      // settings update phone
      return (
        <blockquote className="is-size-6 mt-2">
          {showErrorMessage && (
            <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
              {showErrorMessage}
            </ErrorModal>
          )}
          <form onSubmit={handleSubmit(onSendFromSettings)}>
            <div className="input-container">
              <label className="text-dark dark:text-white mobile-text-color">
                {t("onboarding.verify-enter-phone-desc")}
              </label>
              <input
                className="input max-md:w-full input-responsive-white text-black dark:text-white"
                type="tel"
                inputMode="tel"
                placeholder="(xxx) xxx-xxxx"
                value={formattedNumber}
                onChange={(event) => onTextChange(event.target.value)}
              />
              {formattedNumber.length === 14 && (
                <span className="password-toggle home">
                  <Checkmark />
                </span>
              )}
            </div>
            <p className="mt-4 text-dark dark:text-white text-xs mobile-text-color">
              {t("onboarding.verify-enter-phone-small-print")}
            </p>

            <button
              className={`btn mt-5 docket-button-responsive pl-4 pr-4`}
              type="submit"
              disabled={formattedNumber.length === 14 && !sendFromSettings ? false : true}
            >
              {t("generic.send")}
            </button>
          </form>
        </blockquote>
      );
    } else {
      throw new Error("Sorry, this is not supported");
    }
  };

  return <>{getPhoneLayout()}</>;
}

export default VerifyPhone;
